import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FiExternalLink } from 'react-icons/fi';
import { Container } from '../ui';
import { markerSvgFor } from '../property/propertyMap';
import type { MapPin } from '../property/propertyClusters';
import { Band, Eyebrow, SectionHeading, goldText, ink } from './tokens';

const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const Frame = styled.div`
  position: relative;
  height: clamp(340px, 52vh, 500px);
  margin-top: 2rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 50px -30px rgba(20, 12, 6, 0.45);

  .leaflet-container { height: 100%; width: 100%; font-family: inherit; }

  .place-marker { background: transparent; border: none; }

  .place-marker span {
    position: relative;
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    border-radius: 50% 50% 50% 4px;
    transform: rotate(-45deg);
    background: ${({ theme }) => ink(theme)};
    color: ${({ theme }) => theme.colors.primary};
    border: 3px solid ${({ theme }) => theme.colors.primary};
    box-shadow: 0 10px 22px rgba(20, 12, 6, 0.4);
  }

  .place-marker svg { transform: rotate(45deg); width: 20px; height: 20px; }

  .place-marker b {
    position: absolute;
    top: -9px;
    right: -9px;
    transform: rotate(45deg);
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    font-size: 0.75rem;
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => ink(theme)};
    border: 2px solid #fff;
  }

  .leaflet-popup-content-wrapper { border-radius: 14px; }
  .leaflet-popup-content { margin: 0.9rem 1rem; min-width: 220px; }
`;

const PopupList = styled.ul`
  display: grid;
  gap: 0.5rem;
  max-height: 220px;
  overflow: auto;

  a {
    display: block;
    font-size: 0.86rem;
    line-height: 1.35;
    color: ${({ theme }) => ink(theme)};
    &:hover { color: ${({ theme }) => goldText(theme)}; text-decoration: underline; }
  }
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.colors.neutral[600]};

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 600;
    color: ${({ theme }) => goldText(theme)};
    &:hover { text-decoration: underline; }
  }
`;

interface Group {
  key: string;
  latitude: number;
  longitude: number;
  pins: MapPin[];
}

/** Imóveis no mesmo ponto (um condomínio inteiro no mesmo CEP) viram um marcador. */
const groupByPoint = (pins: MapPin[]): Group[] => {
  const groups = new Map<string, Group>();
  pins.forEach((pin) => {
    const key = `${pin.latitude.toFixed(4)},${pin.longitude.toFixed(4)}`;
    const group = groups.get(key) || { key, latitude: pin.latitude, longitude: pin.longitude, pins: [] };
    group.pins.push(pin);
    groups.set(key, group);
  });
  return Array.from(groups.values());
};

const placeIcon = (count: number, type?: string) =>
  L.divIcon({
    className: 'place-marker',
    html: `<span>${markerSvgFor(type)}${count > 1 ? `<b>${count}</b>` : ''}</span>`,
    iconSize: [46, 46],
    iconAnchor: [23, 44],
    popupAnchor: [0, -40],
  });

const FitBounds: React.FC<{ groups: Group[]; center: [number, number] }> = ({ groups, center }) => {
  const map = useMap();
  const [lat, lng] = center;
  useEffect(() => {
    if (groups.length > 1) {
      map.fitBounds(
        L.latLngBounds(groups.map((g) => [g.latitude, g.longitude] as [number, number])),
        { padding: [60, 60], maxZoom: 16 }
      );
    } else {
      map.setView([lat, lng], 15);
    }
  }, [map, groups, lat, lng]);
  return null;
};

interface Props {
  name: string;
  city?: string | null;
  center: [number, number];
  pins: MapPin[];
}

const LocationMap: React.FC<Props> = ({ name, city, center, pins }) => {
  const groups = useMemo(() => groupByPoint(pins), [pins]);
  const markers: Group[] = groups.length > 0
    ? groups
    : [{ key: 'center', latitude: center[0], longitude: center[1], pins: [] }];
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${center[0]},${center[1]}`;

  return (
    <Band aria-labelledby="local-mapa">
      <Container>
        <Eyebrow>Localização</Eyebrow>
        <SectionHeading id="local-mapa">Onde fica</SectionHeading>
        <Frame>
          <MapContainer center={center} zoom={15} scrollWheelZoom={false}>
            <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
            <FitBounds groups={groups} center={center} />
            {markers.map((group) => (
              <Marker
                key={group.key}
                position={[group.latitude, group.longitude]}
                icon={placeIcon(group.pins.length, group.pins[0]?.type)}
                title={name}
              >
                {group.pins.length > 0 && (
                  <Popup>
                    <PopupList>
                      {group.pins.map((pin) => (
                        <li key={pin.id}><Link to={`/imovel/${pin.id}`}>{pin.title}</Link></li>
                      ))}
                    </PopupList>
                  </Popup>
                )}
              </Marker>
            ))}
          </MapContainer>
        </Frame>
        <Foot>
          <span>{city ? `${name} · ${city}` : name}</span>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
            Abrir no Google Maps <FiExternalLink size={14} />
          </a>
        </Foot>
      </Container>
    </Band>
  );
};

export default LocationMap;
